import { Context } from '@vpap_svelte/core';
import { useFetchState } from '@vpap_svelte/core';
import { rollup, rollups, sum, stack, group, hierarchy } from '@vpap_svelte/charts/d3';
import { MediaQuery } from 'svelte/reactivity';

export class AppData {
	/**
	 * @type {import("@vpap_svelte/core").FetchState<import("./types").DataRecord[], any>}
	 */
	#data;
	/**
	 * @type {MediaQuery}
	 */
	#mq;

	/**
	 * @type {"stackedbar" | "circlepack" | "column"}
	 */
	tipChart = $state(undefined);

	/**
	 *
	 * @param {string} url
	 */
	constructor(url) {
		this.#mq = new MediaQuery('(max-width: 35em)');
		this.#data = useFetchState(url, {}, { transformSuccess: (res) => res.json() });
	}

	/** The actual data */
	get data() {
		return this.#data.data ?? [];
	}

	/**
	 * Gets the total sum of large inaugural fundraising donations per governor
	 */
	get byGovernor() {
		return rollup(
			this.data,
			(v) => sum(v, (d) => d.amount),
			(d) => d.candidate_name,
		);
	}

	/**
	 * Gets the governors in the order they appear
	 */
	get governorOrder() {
		let yrMap = new Map();
		for (const record of this.data) {
			if (!yrMap.has(record.election_year)) {
				yrMap.set(record.election_year, record.candidate_name);
			}
		}
		return [...yrMap.entries()].sort((a, b) => a[0] - b[0]).map((d) => d[1]);
	}

	/**
	 * Gets the types in sort order
	 */
	get donorTypeOrder() {
		return [
			'Lobbying Client',
			'Virginia Individual',
			'Virginia Organization',
			'Out of State Donor',
		];
	}

	get colorMapping() {
		const colors = ['#399F9F', '#F6895A', '#714489', '#FFC000'];
		return new Map(this.donorTypeOrder.map((d, i) => [d, colors[i]]));
	}

	get byDonorAndType() {
		return new Map(
			rollups(
				this.data
					.slice()
					.sort(
						(a, b) =>
							this.governorOrder.findIndex((e) => e === a.candidate_name) -
							this.governorOrder.findIndex((e) => e === b.candidate_name),
					),
				(v) => sum(v, (d) => d.amount),
				(d) => d.candidate_name,
				(d) => d.type,
			),
		);
	}

	/**
	 * Returns stacked bars showing the percentage of each type of donation the governor
	 * received in inaugural donations (using the `type` field)
	 */
	get percentsByDonorType() {
		// First, get the sum by governor and type
		// sort by the order in which the governor should appear (first year appearing 1st)
		const byGovernorAndType = rollups(
			this.data
				.slice()
				.sort(
					(a, b) =>
						this.governorOrder.findIndex((e) => e === a.candidate_name) -
						this.governorOrder.findIndex((e) => e === b.candidate_name),
				),
			(v) => sum(v, (d) => d.amount),
			(d) => d.candidate_name,
			(d) => d.type,
		);

		let byPercent = [];
		for (const [gov, byType] of byGovernorAndType) {
			const totalAmount = byType.reduce((acc, cur) => cur[1] + acc, 0);

			// Create data object with percentages as decimals
			const dataObj = {};
			for (const [type, amount] of byType) {
				dataObj[type] = amount / totalAmount;
			}

			// Use d3 stack
			const stackGenerator = stack().keys(this.donorTypeOrder);
			// @ts-ignore
			const stackedData = stackGenerator([dataObj]);

			// Extract just the [start, end] segments from each series
			const stacked = stackedData.map((series) => series[0]);

			byPercent.push([gov, stacked]);
		}
		return byPercent;
	}

	/**
	 * Gets data about the most recent gubernatorial candidate's donations in the form of a circle pack
	 * broken down by party
	 */
	get packData() {
		const mostRecentGovernor = this.governorOrder[this.governorOrder.length - 1];
		const filteredData = this.data.filter((e) => e.candidate_name === mostRecentGovernor);
		const byType = group(filteredData, (d) => d.type);
		const raw = {
			name: `${mostRecentGovernor} Donors`,
			children: [...byType.entries()].map((d) => {
				return {
					name: d[0],
					children: d[1].map((x) => {
						return {
							name: x.display_name,
							amount: x.amount,
							location: x.display_location,
						};
					}),
				};
			}),
		};
		// @ts-ignore
		return hierarchy(raw, (d) => d.children).sum((d) => d.amount);
	}

	get isMobile() {
		return this.#mq.current;
	}
}

/**
 * @type { Context<AppData>}
 */
export const AppContext = new Context('AppData');
